import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const serif = { fontFamily: "'Playfair Display', serif" };
const NAVY = "#0A2A5A";
const GOLD = "#F9AB0D";

const MAX_POLLS = 15;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | failed
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      polls.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        const ps = res.data.payment_status;
        if (ps === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (ps === "failed" || ps === "expired") {
          setState("failed");
          return;
        }
      } catch (e) {
        // keep polling on transient errors
      }
      if (polls.current >= MAX_POLLS) {
        setState("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: NAVY }} data-testid="payment-success-page">
      <div className="bg-white rounded-3xl p-8 sm:p-12 max-w-md w-full text-center shadow-2xl">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto mb-6 animate-spin" style={{ color: GOLD }} />
            <h1 className="text-2xl mb-2" style={{ ...serif, color: NAVY }} data-testid="payment-checking">Confirming your payment…</h1>
            <p className="text-sm text-slate-500">Just a moment while we verify with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: "rgba(249,171,13,0.15)" }}>
              <CheckCircle2 className="w-9 h-9" style={{ color: GOLD }} />
            </div>
            <h1 className="text-3xl mb-3" style={{ ...serif, color: NAVY }} data-testid="payment-confirmed">Welcome aboard! 🎉</h1>
            <p className="text-sm text-slate-500 mb-8">
              Your subscription is active. Enjoy more matches, unlimited messaging, and everything your new plan unlocks.
            </p>
            <button
              onClick={() => navigate("/matches")}
              className="w-full rounded-full py-3.5 font-bold transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: GOLD, color: NAVY }}
              data-testid="payment-success-continue"
            >
              See Today's Matches
            </button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto mb-6" style={{ color: GOLD }} />
            <h1 className="text-2xl mb-3" style={{ ...serif, color: NAVY }} data-testid="payment-pending">Payment processing</h1>
            <p className="text-sm text-slate-500 mb-8">
              Your payment is still being confirmed. Your plan will activate automatically — check back in a minute.
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="w-full rounded-full py-3.5 font-bold"
              style={{ backgroundColor: "rgba(10,42,90,0.08)", color: NAVY }}
            >
              Back to Plans
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center bg-red-50">
              <XCircle className="w-9 h-9 text-red-500" />
            </div>
            <h1 className="text-2xl mb-3" style={{ ...serif, color: NAVY }} data-testid="payment-failed">Something went wrong</h1>
            <p className="text-sm text-slate-500 mb-8">
              We couldn't confirm your payment. You haven't been charged if the checkout didn't complete.
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="w-full rounded-full py-3.5 font-bold"
              style={{ backgroundColor: GOLD, color: NAVY }}
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
